import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LifeBuoy, Plus, MessageSquare, Clock, AlertCircle, CheckCircle, X, Send } from 'lucide-react';
import { useAppContext, Ticket } from '../context/AppContext';
import { supabase } from '../lib/supabase';
interface SupportTicketPanelProps {
  userType: 'customer' | 'business';
  userName: string;
  userEmail: string;
}
const defaultDepartments = ['Technical Support', 'Billing & Accounts', 'Sales', 'General Inquiry'];
export function SupportTicketPanel({
  userType,
  userName,
  userEmail
}: SupportTicketPanelProps) {
  const {
    tickets,
    addTicket
  } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [departments, setDepartments] = useState<{
    id: string;
    name: string;
  }[]>([]);
  useEffect(() => {
    async function loadDepartments() {
      try {
        const {
          data,
          error
        } = await supabase.from('site_settings').select('value').eq('key', 'departments').maybeSingle();
        if (!error && data?.value) {
          const depts = data.value as any[];
          setDepartments(depts.filter(d => d.isActive).map(d => ({
            id: d.id,
            name: d.name
          })));
        } else {
          setDepartments(defaultDepartments.map(name => ({
            id: name.toLowerCase().replace(/\s+/g, '_'),
            name
          })));
        }
      } catch (err) {
        setDepartments(defaultDepartments.map(name => ({
          id: name.toLowerCase().replace(/\s+/g, '_'),
          name
        })));
      }
    }
    loadDepartments();
  }, []);
  // Filter tickets for this user
  const userTickets = tickets.filter(t => t.clientEmail === userEmail);
  const handleCreateTicket = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const departmentId = formData.get('department') as string;
    const departmentName = departments.find(d => d.id === departmentId)?.name || departmentId;
    const subject = formData.get('subject') as string;
    const priority = formData.get('priority') as Ticket['priority'];
    const message = formData.get('message') as string;
    const newTicket: Ticket = {
      id: `TKT-${Math.floor(1000 + Math.random() * 9000)}`,
      subject: `[${departmentName}] ${subject}`,
      clientName: userName,
      clientEmail: userEmail,
      priority,
      status: 'open',
      department: departmentId,
      messages: [{
        id: `m_${Date.now()}`,
        sender: userName,
        role: 'client',
        content: message,
        createdAt: new Date().toISOString()
      }],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    addTicket(newTicket);
    setIsModalOpen(false);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'in-progress':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      case 'resolved':
        return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'closed':
        return 'bg-slate-800 text-slate-400 border-slate-700';
      default:
        return 'bg-slate-800 text-slate-400 border-slate-700';
    }
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
      case 'critical':
        return 'text-red-500';
      case 'medium':
        return 'text-amber-500';
      case 'low':
        return 'text-emerald-500';
      default:
        return 'text-slate-400';
    }
  };
  return <div className="space-y-8" data-id="element-1919">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1920">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-1921">
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-1922">
            Support Center
          </h1>
          <p className="text-slate-400" data-id="element-1923">
            Manage your support tickets and get help from our team.
          </p>
        </motion.div>
        <button onClick={() => setIsModalOpen(true)} className="bg-fox-600 hover:bg-fox-500 text-white px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors shadow-lg shadow-fox-500/20" data-id="element-1924">
          <Plus size={18} data-id="element-1925" /> Create Ticket
        </button>
      </div>

      {/* Tickets List */}
      <div className="space-y-4" data-id="element-1926">
        {userTickets.length === 0 ? <div className="bg-slate-950 border border-slate-800 rounded-2xl p-12 text-center" data-id="element-1927">
            <LifeBuoy size={48} className="mx-auto text-slate-700 mb-4" data-id="element-1928" />
            <h3 className="text-xl font-bold text-white mb-2" data-id="element-1929">
              No Support Tickets
            </h3>
            <p className="text-slate-500 mb-6" data-id="element-1930">
              You don't have any open or past support tickets.
            </p>
            <button onClick={() => setIsModalOpen(true)} className="bg-slate-800 hover:bg-slate-700 text-white px-6 py-2 rounded-lg font-medium transition-colors" data-id="element-1931">
              Open a Ticket
            </button>
          </div> : userTickets.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).map((ticket, i) => <motion.div key={ticket.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.1
      }} className="bg-slate-950 border border-slate-800 rounded-2xl p-6 hover:border-slate-700 transition-colors cursor-pointer group" data-id="element-1932">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1933">
                  <div className="flex-1" data-id="element-1934">
                    <div className="flex items-center gap-3 mb-2" data-id="element-1935">
                      <span className="text-sm font-mono text-slate-500" data-id="element-1936">
                        {ticket.id}
                      </span>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`} data-id="element-1937">
                        {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                      </span>
                      <span className={`text-xs font-medium flex items-center gap-1 ${getPriorityColor(ticket.priority)}`} data-id="element-1938">
                        <AlertCircle size={12} data-id="element-1939" />
                        {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}{' '}
                        Priority
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-white group-hover:text-fox-500 transition-colors" data-id="element-1940">
                      {ticket.subject}
                    </h3>
                  </div>
                  <div className="flex items-center gap-6 text-sm text-slate-400" data-id="element-1941">
                    <div className="flex items-center gap-2" data-id="element-1942">
                      <MessageSquare size={16} data-id="element-1943" />
                      <span data-id="element-1944">{ticket.messages.length} messages</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1945">
                      <Clock size={16} data-id="element-1946" />
                      <span data-id="element-1947">
                        Updated{' '}
                        {new Date(ticket.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>)}
      </div>

      {/* Create Ticket Modal */}
      <AnimatePresence data-id="element-1948">
        {isModalOpen && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-1949">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-1950">
              <div className="p-6 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-slate-900 z-10" data-id="element-1951">
                <h2 className="text-xl font-bold text-white flex items-center gap-2" data-id="element-1952">
                  <LifeBuoy size={20} className="text-fox-500" data-id="element-1953" />
                  Create Support Ticket
                </h2>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white" data-id="element-1954">
                  <X size={24} data-id="element-1955" />
                </button>
              </div>

              <form onSubmit={handleCreateTicket} className="p-6 space-y-6" data-id="element-1956">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1957">
                  <div className="space-y-2" data-id="element-1958">
                    <label className="text-sm font-medium text-slate-300" data-id="element-1959">
                      Department
                    </label>
                    <select name="department" required className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500 appearance-none" data-id="element-1960">
                      {departments.map(dept => <option key={dept.id} value={dept.id} data-id="element-1961">
                          {dept.name}
                        </option>)}
                    </select>
                  </div>

                  <div className="space-y-2" data-id="element-1962">
                    <label className="text-sm font-medium text-slate-300" data-id="element-1963">
                      Priority Level *
                    </label>
                    <select name="priority" required defaultValue="medium" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-1964">
                      <option value="low" data-id="element-1965">Low - General Question</option>
                      <option value="medium" data-id="element-1966">Medium - Issue / Bug</option>
                      <option value="high" data-id="element-1967">High - Service Down / Urgent</option>
                    </select>
                  </div>

                  <div className="space-y-2 md:col-span-2" data-id="element-1968">
                    <label className="text-sm font-medium text-slate-300" data-id="element-1969">
                      Subject *
                    </label>
                    <input name="subject" required placeholder="Brief description of the issue" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-1970" />
                  </div>

                  <div className="space-y-2 md:col-span-2" data-id="element-1971">
                    <label className="text-sm font-medium text-slate-300" data-id="element-1972">
                      Message Details *
                    </label>
                    <textarea name="message" required rows={6} placeholder="Please provide as much detail as possible so we can help you quickly..." className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none resize-none" data-id="element-1973" />
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-800 flex justify-end gap-3" data-id="element-1974">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white transition-colors" data-id="element-1975">
                    Cancel
                  </button>
                  <button type="submit" className="bg-fox-600 hover:bg-fox-500 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2" data-id="element-1976">
                    <Send size={16} data-id="element-1977" /> Submit Ticket
                  </button>
                </div>
              </form>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}